/**
 * AcceptDesignation - Public page for accepting staff designations
 *
 * Rendered at /join/d_:designationId (UnifiedJoinPage). Legacy /staff/accept/:id redirects to /join/d_:id.
 * Users must be logged in to accept. If not logged in, they are prompted to log in or create an account first.
 *
 * @param {string} [designationId] - Optional; when provided (e.g. from UnifiedJoinPage), used instead of URL param.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Business,
  CheckCircle,
  Cancel,
  HourglassEmpty,
  Login,
  Warning,
  LocalHospital,
} from '@mui/icons-material';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@healthdesk/shared-hooks';
import {
  getDesignation,
  acceptDesignation,
  declineDesignation,
  DESIGNATION_STATUS,
} from '@healthdesk/shared-services';
import {
  sendDesignationAcceptedNotification,
  sendDesignationDeclinedNotification,
} from '@healthdesk/shared-services';

// Role display names
const ROLE_LABELS = {
  'facility': 'Facility Administrator',
  'department-admin': 'Department Administrator',
  'chartmind-supervisor': 'Clinic Manager',
  'professional-chartmind': 'Healthcare Professional',
};

/**
 * Main AcceptDesignation component
 */
const AcceptDesignation = ({ designationId: designationIdProp }) => {
  const { designationId: designationIdParam } = useParams();
  const designationId = designationIdProp ?? designationIdParam;
  const navigate = useNavigate();
  const { user, userData, loading: authLoading } = useAuth();
  
  // State
  const [designation, setDesignation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [declined, setDeclined] = useState(false);
  
  // Decline dialog
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  
  // Load designation
  useEffect(() => {
    loadDesignation();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designationId]);
  
  const loadDesignation = async () => {
    if (!designationId) {
      setError('Invalid designation link. Please check the URL.');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await getDesignation(designationId);
      
      if (!data) {
        setError('Designation not found. The link may be invalid or expired.');
        setLoading(false);
        return;
      }
      
      setDesignation(data);
    } catch (err) {
      console.error('[AcceptDesignation] Error loading designation:', err);
      setError('Error loading designation details. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle accept
  const handleAccept = async () => {
    if (!user) {
      setError('Please log in to accept this designation.');
      return;
    }
    
    if (userData?.email?.toLowerCase() !== designation?.email?.toLowerCase()) {
      setError(`This designation is for ${designation.email}. You are logged in as ${userData?.email}. Please log in with the correct account.`);
      return;
    }
    
    setProcessing(true);
    setError(null);
    
    try {
      await acceptDesignation(designationId, user.uid);
      
      // Send notification to designator
      await sendDesignationAcceptedNotification(designation, userData);
      
      setAccepted(true);
    } catch (err) {
      console.error('[AcceptDesignation] Error accepting:', err);
      setError(err.message || 'Error accepting designation. Please try again.');
    } finally {
      setProcessing(false);
    }
  };
  
  // Handle decline
  const handleDecline = async () => {
    setProcessing(true);
    setError(null);
    
    try {
      await declineDesignation(designationId, declineReason);
      
      // Send notification to designator
      await sendDesignationDeclinedNotification(designation);
      
      setDeclined(true);
      setDeclineDialogOpen(false);
    } catch (err) {
      console.error('[AcceptDesignation] Error declining:', err);
      setError(err.message || 'Error declining designation. Please try again.');
    } finally {
      setProcessing(false);
    }
  };
  
  // Render loading state
  if (loading || authLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        bgcolor: 'grey.100',
      }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography>Loading designation...</Typography>
        </Paper>
      </Box>
    );
  }
  
  // Render error state
  if (error && !designation) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        bgcolor: 'grey.100',
        p: 3,
      }}>
        <Paper sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
          <Warning sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Unable to Load Designation
          </Typography>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Button
            variant="contained"
            component={Link}
            to="/"
          >
            Go to Home
          </Button>
        </Paper>
      </Box>
    );
  }
  
  // Check designation status
  const isExpired = designation?.status === DESIGNATION_STATUS.EXPIRED || 
    (designation?.expiresAt?.toDate?.() < new Date());
  const isAlreadyAccepted = designation?.status === DESIGNATION_STATUS.ACCEPTED;
  const isAlreadyDeclined = designation?.status === DESIGNATION_STATUS.DECLINED;
  const isRevoked = designation?.status === DESIGNATION_STATUS.REVOKED;
  const isPending = designation?.status === DESIGNATION_STATUS.PENDING && !isExpired;
  
  // Render accepted state
  if (accepted) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        bgcolor: 'grey.100',
        p: 3,
      }}>
        <Paper sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Designation Accepted!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            You have been added as {ROLE_LABELS[designation.designatedRole] || designation.designatedRole}. 
            You can now access the facility's tools and features.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/dashboard')}
          >
            Go to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }
  
  // Render declined state
  if (declined) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        bgcolor: 'grey.100',
        p: 3,
      }}>
        <Paper sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
          <Cancel sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Designation Declined
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            You have declined this staff designation. The inviting supervisor has been notified.
          </Typography>
          <Button
            variant="contained"
            component={Link}
            to="/"
          >
            Go to Home
          </Button>
        </Paper>
      </Box>
    );
  }
  
  // Render main content
  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: 'grey.100',
      py: 4,
      px: 2,
    }}>
      <Box sx={{ maxWidth: 600, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <LocalHospital sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
          <Typography variant="h4" gutterBottom>
            Staff Designation
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You have been invited to join a healthcare facility
          </Typography>
        </Box>
        
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {/* Designation Details Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Business fontSize="large" color="primary" />
              <Box>
                <Typography variant="h6">
                  Facility Invitation
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Facility ID: {designation?.facilityId}
                </Typography>
              </Box>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Designated For */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Designated For:
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {designation?.email}
                </Typography>
              </Box>
              
              {/* Role */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Assigned Role:
                </Typography>
                <Chip 
                  label={ROLE_LABELS[designation?.designatedRole] || designation?.designatedRole}
                  color="primary"
                  size="small"
                />
              </Box>
              
              {/* Status */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Status:
                </Typography>
                <Chip
                  label={
                    isExpired ? 'Expired' :
                    isAlreadyAccepted ? 'Already Accepted' :
                    isAlreadyDeclined ? 'Already Declined' :
                    isRevoked ? 'Revoked' :
                    'Pending'
                  }
                  color={
                    isExpired || isRevoked ? 'error' :
                    isAlreadyAccepted ? 'success' :
                    isAlreadyDeclined ? 'warning' :
                    'warning'
                  }
                  icon={
                    isExpired || isRevoked ? <Cancel /> :
                    isAlreadyAccepted ? <CheckCircle /> :
                    isAlreadyDeclined ? <Cancel /> :
                    <HourglassEmpty />
                  }
                  size="small"
                />
              </Box>
              
              {/* Expiry Date */}
              {designation?.expiresAt && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Expires:
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color={isExpired ? 'error.main' : 'text.secondary'}
                  >
                    {designation.expiresAt.toDate?.().toLocaleDateString()}
                  </Typography>
                </Box>
              )}
              
              {/* Department (if specified) */}
              {designation?.departmentId && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Department:
                  </Typography>
                  <Typography variant="body2">
                    {designation.departmentId}
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
        
        {/* Status-specific messages and actions */}
        {isExpired && (
          <Alert severity="error" sx={{ mb: 3 }}>
            This designation has expired. Please contact the person who invited you to request a new invitation.
          </Alert>
        )}
        
        {isAlreadyAccepted && (
          <Alert severity="info" sx={{ mb: 3 }}>
            This designation has already been accepted. You should have access to the facility.
            <Button
              sx={{ mt: 1 }}
              variant="outlined"
              size="small"
              onClick={() => navigate('/dashboard')}
            >
              Go to Dashboard
            </Button>
          </Alert>
        )}
        
        {isAlreadyDeclined && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            This designation was previously declined. Contact the inviting supervisor if you'd like to reconsider.
          </Alert>
        )}
        
        {isRevoked && (
          <Alert severity="error" sx={{ mb: 3 }}>
            This designation has been revoked by the supervisor.
          </Alert>
        )}
        
        {/* Not logged in */}
        {!user && isPending && (
          <Alert severity="info" icon={<Login />} sx={{ mb: 3 }}>
            Please log in to accept this designation. You must log in with the email address: <strong>{designation?.email}</strong>
            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                component={Link}
                to={`/auth?redirect=${encodeURIComponent(`/join/d_${designationId}`)}`}
              >
                Log In
              </Button>
            </Box>
          </Alert>
        )}
        
        {/* Wrong email */}
        {user && isPending && userData?.email?.toLowerCase() !== designation?.email?.toLowerCase() && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            This designation is for <strong>{designation.email}</strong>, but you are logged in as <strong>{userData?.email}</strong>. 
            Please log in with the correct account to accept this invitation.
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                component={Link}
                to={`/auth?redirect=${encodeURIComponent(`/join/d_${designationId}`)}`}
              >
                Switch Account
              </Button>
            </Box>
          </Alert>
        )}
        
        {/* Action buttons for valid pending designation */}
        {user && isPending && userData?.email?.toLowerCase() === designation?.email?.toLowerCase() && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Would you like to accept this designation?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              By accepting, you will be added to the facility as {ROLE_LABELS[designation.designatedRole] || designation.designatedRole} and gain access to its tools and features.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setDeclineDialogOpen(true)}
                disabled={processing}
              >
                Decline
              </Button>
              <Button
                variant="contained"
                onClick={handleAccept}
                disabled={processing}
                startIcon={processing ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
              >
                {processing ? 'Processing...' : 'Accept Designation'}
              </Button>
            </Box>
          </Paper>
        )}
      </Box>
      
      {/* Decline Dialog */}
      <Dialog 
        open={declineDialogOpen} 
        onClose={() => setDeclineDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Decline Designation</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to decline this staff designation? You can optionally provide a reason.
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason (Optional)"
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            placeholder="e.g., I've joined another facility, this was sent in error..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeclineDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDecline}
            disabled={processing}
          >
            {processing ? 'Processing...' : 'Decline'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AcceptDesignation;
